import type { RootState } from 'src/redux/store';
import { useSelector } from 'src/redux/store';
import {
  useUpdateAssistantResourceMutation,
  useUpdateProjectResourceMutation,
  useUpdateResourceMutation,
  useUpdateSmartMeetResourceMutation
} from 'src/services/api';
import type { StringKeys } from 'src/types/base';
import { USER_APP_PERMISSIONS } from 'src/types/enum';
import { ErrorContext } from 'src/utils/errorMappings';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { SYSADMIN_REALM } from 'src/utils/utils';

import useMutation from './useMutation';

interface UpdateAccessRightsParams {
  id: number;
  role: string;
  addedUsers?: StringKeys[];
  removedUsers?: StringKeys[];
  assignmentType?: string;
  type_id?: string;
  isAssistant?: boolean;
  isSmartMeet?: boolean;
  isProject?: boolean;
  successMessage?: string;
  onClose?: () => void;
}

export type AccessRightsUpdateHandlerType = (
  params: UpdateAccessRightsParams
) => Promise<void>;

const useUpdateAccessRights = () => {
  const { tenant_info } = useSelector((state: RootState) => state.library);

  const [updateAccessRights, { isLoading: isUpdatingResourceAccess }] =
    useMutation({
      api: useUpdateResourceMutation,
      errorContext: ErrorContext.FILES
    });
  const [
    updateAssistantAccessRights,
    { isLoading: isUpdatingAssistantAccess }
  ] = useMutation({
    api: useUpdateAssistantResourceMutation,
    errorContext: ErrorContext.ASSISTANT
  });

  const [
    updateSmartMeetAccessRights,
    { isLoading: isUpdatingSmartMeetAccess }
  ] = useMutation({
    api: useUpdateSmartMeetResourceMutation,
    errorContext: ErrorContext.SMART_MEET
  });

  const [updateProjectAccessRights, { isLoading: isUpdatingProjectAccess }] =
    useMutation({
      api: useUpdateProjectResourceMutation,
      errorContext: ErrorContext.GENERAL
    });

  const userAppPermissions = useSelector(
    (state: RootState) => state.data?.userAppPermissions
  );

  const isSysAdminUser = !!(
    userAppPermissions?.[USER_APP_PERMISSIONS.ADMIN_CONSOLE]?.value &&
    tenant_info?.name === SYSADMIN_REALM
  );

  const handleUpdateAccessRights: AccessRightsUpdateHandlerType = async ({
    id,
    role,
    addedUsers = [],
    removedUsers = [],
    assignmentType,
    type_id,
    isAssistant = false,
    isSmartMeet = false,
    isProject = false,
    successMessage = T.resourceSharingSuccessMessage,
    onClose
  }) => {
    const addPerms = addedUsers.length
      ? addedUsers.map((user) => ({
          user_group: user.value || user,
          // Entries staged by the "Shared with" role dropdown carry their own
          // role; newly invited assignees fall back to the Invite-tab role.
          role: user.role || role,
          type_id: user.type_id || type_id
        }))
      : [];
    const removePerms = removedUsers?.length
      ? removedUsers.map((accessRight) => ({
          user_group: accessRight.value || accessRight,
          role: accessRight.role,
          type_id: accessRight.type_id
        }))
      : [];
    const updateAccessRightsParams = {
      params: {
        params: {
          op: 'set-perms',
          id: id,
          add_perms: JSON.stringify(addPerms),
          remove_perms: JSON.stringify(removePerms),
          assignment_type: assignmentType || 'default'
        }
      },
      fallbackMsg: T.resourceSharingErrorMessage,
      successMsg: successMessage,
      successCallback: () => onClose && onClose?.()
    };

    if (isAssistant) {
      await updateAssistantAccessRights(updateAccessRightsParams);
    } else if (isProject) {
      await updateProjectAccessRights(updateAccessRightsParams);
    } else if (isSmartMeet) {
      await updateSmartMeetAccessRights(updateAccessRightsParams);
    } else {
      await updateAccessRights(updateAccessRightsParams);
    }
  };

  const isUpdatingAccess =
    isUpdatingResourceAccess ||
    isUpdatingAssistantAccess ||
    isUpdatingProjectAccess ||
    isUpdatingSmartMeetAccess;

  return {
    handleUpdateAccessRights,
    isUpdatingAccess,
    isSysAdminUser
  };
};

export default useUpdateAccessRights;
